import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "@material-ui/core/IconButton";
import { Checkbox, FormControlLabel } from "@mui/material";
import { Helmet } from "react-helmet";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import api from "../../services/api";
import config from "../../config";

// Ícones
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import GoogleIcon from '@mui/icons-material/Google';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import TelegramIcon from '@mui/icons-material/Telegram';
import FacebookIcon from '@mui/icons-material/Facebook';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SecurityIcon from '@mui/icons-material/Security';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const { nomeEmpresa, versionSystem } = config;

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    minHeight: "100vh",
    display: "flex",
    backgroundColor: theme.palette.background.default,
    overflow: "hidden",
    position: "relative",
  },
  
  // Seção esquerda - Slides com fundo escuro
  leftSection: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#4c1d95",
    backgroundImage: `linear-gradient(135deg, #4c1d95 0%, #1e1b4b 100%)`,
    position: "relative",
    overflow: "hidden",
    padding: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      display: "none",
    },
    // Efeito de malha decorativa
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundImage: `radial-gradient(circle at 20% 30%, rgba(101, 23, 171, 0.15) 0%, transparent 50%), 
                        radial-gradient(circle at 80% 70%, rgba(124, 58, 237, 0.15) 0%, transparent 50%)`,
      pointerEvents: "none",
    }
  },

  slideContentWrapper: {
    width: "100%",
    maxWidth: "600px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    zIndex: 2,
  },

  slideContainer: {
    width: "100%",
    height: "auto",
    minHeight: "400px",
    position: "relative",
    marginBottom: theme.spacing(6),
  },

  slide: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transform: "translateY(20px)",
    transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
    "&.active": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  slideContent: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: theme.spacing(5, 4),
    backgroundColor: "rgba(255, 255, 255, 0.04)",
    backdropFilter: "blur(12px)",
    borderRadius: "32px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    boxShadow: "0 24px 48px rgba(0, 0, 0, 0.2)",
  },

  slideTitle: {
    fontSize: "2.25rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: theme.spacing(2),
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
    textShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
    [theme.breakpoints.down('lg')]: {
      fontSize: "2rem",
    },
  },

  slideDescription: {
    fontSize: "1.125rem",
    color: "#cbd5e1",
    lineHeight: 1.6,
    marginBottom: theme.spacing(4),
    maxWidth: "500px",
    fontWeight: 400,
  },

  socialIconsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    flexWrap: "wrap",
    justifyContent: "center",
  },

  socialIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },

  whatsappIcon: {
    backgroundColor: "#25D366",
    color: "white",
  },

  instagramIcon: {
    background: "linear-gradient(45deg, #405DE6, #5851DB, #833AB4, #C13584, #E1306C, #FD1D1D)",
    color: "white",
  },

  telegramIcon: {
    backgroundColor: "#0088cc",
    color: "white",
  },

  facebookIcon: {
    backgroundColor: "#1877F2",
    color: "white",
  },

  securityIcon: {
    backgroundColor: "rgba(255, 202, 40, 0.15)",
    color: "#ffca28",
    border: "1px solid rgba(255, 202, 40, 0.3)",
    boxShadow: "0 8px 16px rgba(255, 202, 40, 0.1)",
  },

  // Controles do slide
  slideControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(4),
    marginTop: theme.spacing(2),
    width: "100%",
  },

  slideButton: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      transform: "translateY(-2px)",
    },
  },

  slideButtonIcon: {
    color: "white",
    fontSize: "18px",
  },

  slideIndicators: {
    display: "flex",
    gap: theme.spacing(1),
  },

  indicator: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&.active": {
      backgroundColor: "#6517ab",
      width: "24px",
      borderRadius: "4px",
    },
  },

  // Seção direita - Formulário
  rightSection: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.background.default,
    position: "relative",
    overflow: "auto",
    padding: theme.spacing(4),
  },

  loginCard: {
    width: "100%",
    maxWidth: "440px",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "24px",
    padding: theme.spacing(5),
    boxShadow: theme.palette.type === 'light' 
      ? "0 20px 40px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)"
      : "0 20px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)",
    position: "relative",
    zIndex: 2,
  },

  cardHeader: {
    marginBottom: theme.spacing(4),
  },

  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
  },

  logoImg: {
    maxWidth: "280px",
    maxHeight: "80px",
    width: "auto",
    height: "auto",
    objectFit: "contain",
  },

  welcomeTitle: {
    fontSize: "2rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    textAlign: "center",
    letterSpacing: "-0.02em",
  },

  welcomeSubtitle: {
    fontSize: "0.95rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
    textAlign: "center",
    fontWeight: 400,
    lineHeight: 1.6,
  },

  form: {
    width: "100%",
  },

  inputField: {
    marginBottom: theme.spacing(3),
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      backgroundColor: theme.palette.type === 'light' ? "#f8fafc" : "rgba(255, 255, 255, 0.05)",
      border: `2px solid ${theme.palette.divider}`,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      fontSize: "0.95rem",
      fontWeight: 500,
      "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.type === 'light' ? "#ffffff" : "rgba(255, 255, 255, 0.08)",
      },
      "&.Mui-focused": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.type === 'light' ? "#ffffff" : "rgba(255, 255, 255, 0.08)",
        boxShadow: `0 0 0 4px ${theme.palette.primary.main}20`,
      },
      "& fieldset": {
        border: "none",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "16px 18px",
      color: theme.palette.text.primary,
      fontSize: "0.95rem",
      "&::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 0.7,
      },
    },
  },

  inputIcon: {
    color: "#6517ab",
    fontSize: "1.2rem",
  },

  rememberMeContainer: {
    marginBottom: theme.spacing(4),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  rememberMe: {
    "& .MuiFormControlLabel-label": {
      fontSize: "0.875rem",
      color: "#475569",
      fontWeight: 500,
    },
    "& .MuiCheckbox-root": {
      color: "#94a3b8",
      "&.Mui-checked": {
        color: "#6517ab",
      },
    },
  },

  forgotPassword: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontSize: "0.875rem",
    fontWeight: 600,
    transition: "all 0.2s ease",
    "&:hover": {
      color: theme.palette.primary.dark,
      textDecoration: "underline",
    },
  },

  loginButton: {
    width: "100%",
    height: "52px",
    borderRadius: "14px",
    background: theme.palette.type === 'light' 
      ? "linear-gradient(135deg, #6517ab 0%, #7c3aed 100%)"
      : "linear-gradient(135deg, hsl(270, 80%, 60%) 0%, hsl(280, 80%, 60%) 100%)",
    color: "white",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    marginBottom: theme.spacing(3),
    boxShadow: theme.palette.type === 'light'
      ? "0 8px 24px rgba(101, 23, 171, 0.4)"
      : "0 8px 24px rgba(0, 0, 0, 0.3)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.palette.type === 'light'
        ? "0 12px 32px rgba(101, 23, 171, 0.5)"
        : "0 12px 32px rgba(0, 0, 0, 0.4)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },

  googleButton: {
    width: "100%",
    height: "52px",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontSize: "0.95rem",
    fontWeight: 600,
    textTransform: "none",
    border: "2px solid #e2e8f0",
    marginBottom: theme.spacing(3),
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#f8fafc",
      borderColor: "#cbd5e1",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    },
  },

  signupLink: {
    textAlign: "center",
    color: "#64748b",
    fontSize: "0.875rem",
    marginTop: theme.spacing(4),
    "& a": {
      color: "#6517ab",
      textDecoration: "none",
      fontWeight: 600,
      marginLeft: theme.spacing(0.5),
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },

  securityBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'light' ? "#f8fafc" : "rgba(255, 255, 255, 0.05)",
    borderRadius: "12px",
    border: `1px solid ${theme.palette.divider}`,
  },

  securityText: {
    fontSize: "0.75rem",
    color: "#64748b",
    fontWeight: 500,
  },

  footer: {
    marginTop: theme.spacing(5),
    textAlign: "center",
    paddingTop: theme.spacing(4),
    borderTop: "1px solid #f1f5f9",
  },

  copyright: {
    color: "#94a3b8",
    fontSize: "0.75rem",
    fontWeight: 400,
  },

  version: {
    color: "#6517ab",
    fontSize: "0.75rem",
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
  },

  visibilityIcon: {
    color: "#94a3b8",
    "&:hover": {
      color: "#64748b",
    },
  },

  // Elementos decorativos
  decorationCircle: {
    position: "absolute",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(101, 23, 171, 0.1) 0%, rgba(101, 23, 171, 0) 70%)",
    pointerEvents: "none",
  },

  // Indicador de plataforma
  platformIndicator: {
    position: "absolute",
    bottom: theme.spacing(4),
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(3),
  },

  platformItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "0.75rem",
    fontWeight: 500,
  },

  platformDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#6517ab",
  },
}));

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName } = colorMode;
  const [user, setUser] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [allowSignup, setAllowSignup] = useState(false);
  const { getPublicSetting } = useSettings();
  const { handleLogin } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [systemVersion, setSystemVersion] = useState("");

  // Dados dos slides
  const slides = [
    {
      id: 1,
      title: "Centralize Todas as Conversas",
      description: "Conecte WhatsApp, Instagram, Telegram e Facebook em uma única plataforma. Gerencie todas as mensagens de diferentes redes sociais em um só lugar.",
      icons: [
        { icon: <WhatsAppIcon />, className: classes.whatsappIcon },
        { icon: <InstagramIcon />, className: classes.instagramIcon },
        { icon: <TelegramIcon />, className: classes.telegramIcon },
        { icon: <FacebookIcon />, className: classes.facebookIcon },
      ]
    },
    {
      id: 2,
      title: "Dashboard Inteligente",
      description: "Controle total sobre suas conversas com métricas em tempo real, relatórios avançados e automação inteligente de atendimento.",
      icons: [
        { icon: <DashboardIcon />, className: classes.whatsappIcon },
        { icon: <SyncAltIcon />, className: classes.instagramIcon },
        { icon: <ChatBubbleIcon />, className: classes.telegramIcon },
        { icon: <RocketLaunchIcon />, className: classes.facebookIcon },
      ]
    },
    {
      id: 3,
      title: "Segurança e Confiança",
      description: "Plataforma com criptografia de ponta a ponta, conformidade com LGPD e backups automáticos para garantir a segurança dos seus dados.",
      icons: [
        { icon: <SecurityIcon />, className: classes.securityIcon },
        { icon: <VerifiedUserIcon />, className: classes.securityIcon },
        { icon: <LockIcon />, className: classes.securityIcon },
        { icon: <SyncAltIcon />, className: classes.securityIcon },
      ]
    },
  ];

  // Rotação automática dos slides
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleChangeInput = (name, value) => {
    setUser({ ...user, [name]: value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    getPublicSetting("allowSignup")
      .then((data) => {
        setAllowSignup(data === "enabled");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });

    const fetchVersion = async () => {
      try {
        const { data } = await api.get("/version");
        setSystemVersion(data.version);
      } catch (err) {
        console.log("Error fetching system version", err);
      }
    };
    fetchVersion();
  }, []);

  return (
    <>
      <Helmet>
        <title>{appName || "Chat Platform - Login"}</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>
      
      <CssBaseline />
      
      <div className={classes.root}>
        {/* Seção Esquerda - Slides */}
        <div className={classes.leftSection}>
          {/* Elementos decorativos */}
          <div className={classes.decorationCircle} style={{ top: '-10%', right: '-5%', width: '300px', height: '300px' }} />
          <div className={classes.decorationCircle} style={{ bottom: '-8%', left: '-4%', width: '200px', height: '200px' }} />
          
          <div className={classes.slideContentWrapper}>
            <div className={classes.slideContainer}>
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`${classes.slide} ${index === activeSlide ? 'active' : ''}`}
                >
                  <div className={classes.slideContent}>
                    <Typography className={classes.slideTitle}>
                      {slide.title}
                    </Typography>
                    
                    <Typography className={classes.slideDescription}>
                      {slide.description}
                    </Typography>
                    
                    <div className={classes.socialIconsContainer}>
                      {slide.icons.map((iconData, idx) => (
                        <div key={idx} className={`${classes.socialIcon} ${iconData.className}`}>
                          {React.cloneElement(iconData.icon, { 
                            style: { 
                              fontSize: "24px" 
                            } 
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Controles do Slide */}
            <div className={classes.slideControls}>
              <div className={classes.slideButton} onClick={prevSlide}>
                <ArrowBackIosNewIcon className={classes.slideButtonIcon} />
              </div>
              
              <div className={classes.slideIndicators}>
                {slides.map((_, index) => (
                  <div
                    key={index}
                    className={`${classes.indicator} ${index === activeSlide ? 'active' : ''}`}
                    onClick={() => setActiveSlide(index)}
                  />
                ))}
              </div>
              
              <div className={classes.slideButton} onClick={nextSlide}>
                <ArrowForwardIosIcon className={classes.slideButtonIcon} />
              </div>
            </div>

            {/* Plataformas suportadas */}
            <div className={classes.platformIndicator}>
              <div className={classes.platformItem}>
                <div className={classes.platformDot} />
                <span>WhatsApp Business</span>
              </div>
              <div className={classes.platformItem}>
                <div className={classes.platformDot} />
                <span>Instagram Direct</span>
              </div>
              <div className={classes.platformItem}>
                <div className={classes.platformDot} />
                <span>Telegram</span>
              </div>
            </div>
          </div>
        </div>

        {/* Seção Direita - Formulário */}
        <div className={classes.rightSection}>
          <div className={classes.loginCard}>
            {/* Cabeçalho */}
            <div className={classes.cardHeader}>
              <div className={classes.logoContainer}>
                <img 
                  src={(theme.palette.type === 'light' ? theme.calculatedLogoLight() : theme.calculatedLogoDark())} 
                  className={classes.logoImg} 
                  alt="logo" 
                />
              </div>

              <Typography className={classes.welcomeTitle}>
                Bem-vindo de volta!
              </Typography>
              <Typography className={classes.welcomeSubtitle}>
                Acesse sua conta para gerenciar todas as suas conversas.
              </Typography>
            </div>

            {/* Formulário */}
            <form className={classes.form} noValidate onSubmit={handlSubmit}>
              {/* Email */}
              <TextField
                className={classes.inputField}
                variant="outlined"
                required
                fullWidth
                id="email"
                name="email"
                placeholder="seu@email.com"
                value={user.email}
                onChange={(e) =>
                  handleChangeInput(e.target.name, e.target.value.toLowerCase())
                }
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon className={classes.inputIcon} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Senha */}
              <TextField
                className={classes.inputField}
                variant="outlined"
                required
                fullWidth
                name="password"
                placeholder="••••••••"
                type={showPassword ? "text" : "password"}
                id="password"
                value={user.password}
                onChange={(e) =>
                  handleChangeInput(e.target.name, e.target.value)
                }
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon className={classes.inputIcon} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        className={classes.visibilityIcon}
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Remember Me & Forgot Password */}
              <div className={classes.rememberMeContainer}>
                <FormControlLabel
                  className={classes.rememberMe}
                  control={
                    <Checkbox
                      checked={user.rememberMe}
                      onChange={(e) =>
                        handleChangeInput("rememberMe", e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label="Lembrar por 30 dias"
                />
                <Link 
                  component={RouterLink} 
                  to="/forgetpsw" 
                  className={classes.forgotPassword}
                >
                  Esqueceu a senha?
                </Link>
              </div>

              {/* Botão de Login */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                className={classes.loginButton}
                endIcon={<ArrowForwardIcon />}
              >
                Entrar na Plataforma
              </Button>

              {/* Botão do Google */}
              {/* <Button
                variant="outlined"
                className={classes.googleButton}
                startIcon={<GoogleIcon />}
              >
                Continuar com Google
              </Button> */}

              {/* Link para Signup */}
              {allowSignup && (
                <Typography className={classes.signupLink}>
                  Não tem uma conta? 
                  <Link component={RouterLink} to="/signup">
                    Criar conta agora
                  </Link>
                </Typography>
              )}

              {/* Badge de Segurança */}
              <div className={classes.securityBadge}>
                <SecurityIcon style={{ fontSize: "16px", color: "#6517ab" }} />
                <Typography className={classes.securityText}>
                  Conexão segura • Criptografia SSL/TLS
                </Typography>
              </div>
            </form>

            {/* Footer */}
            <div className={classes.footer}>
              <Typography className={classes.copyright}>
                © {new Date().getFullYear()} {nomeEmpresa}
              </Typography>
              {systemVersion && (
                <Typography className={classes.version}>
                  v{systemVersion}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;